import { Modal } from "@mui/material";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faEnvelope,
  faRotateRight,
  faCheck,
  faCircleExclamation,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import ActionButton from "../ui/ActionButton";
import "../../style/TwoFactorModal.css";

function TwoFactorModal({ open, onClose, onVerificationSuccess, darkMode }) {
  const { t } = useTranslation();
  const [twoFACode, setTwoFACode] = useState(["", "", "", "", "", ""]);
  const [twoFAError, setTwoFAError] = useState("");
  const [twoFASuccess, setTwoFASuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifySuccess, setVerifySuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = useRef([]);

  // reset all state when modal opens
  useEffect(() => {
    if (open) {
      setTwoFACode(["", "", "", "", "", ""]);
      setTwoFAError("");
      setTwoFASuccess("");
      setLoading(false);
      setVerifySuccess(false);
      setResendLoading(false);
      // focus first input after mount
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [open]);

  const handle2FASubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }
    const code = twoFACode.join("");
    setTwoFAError("");
    setTwoFASuccess("");
    setLoading(true);
    setVerifySuccess(false);

    try {
      if (code === "000000") {
        setTimeout(() => {
          setLoading(false);
          setVerifySuccess(true);

          setTimeout(() => {
            onVerificationSuccess();
          }, 500);
        }, 1500);
      } else {
        setTwoFAError(t("Invalid or expired verification code"));
        setLoading(false);
      }
    } catch (error) {
      setTwoFAError(t("Verification failed. Please try again."));
      setLoading(false);
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedValue = e.clipboardData.getData("text").replace(/\D/g, "");

    if (pastedValue.length <= 6) {
      const newCode = [...twoFACode];
      for (let i = 0; i < pastedValue.length; i++) {
        newCode[i] = pastedValue[i];
      }
      setTwoFACode(newCode);
      const nextFocusIndex =
        pastedValue.length === 6 ? 5 : pastedValue.length - 1;
      inputRefs.current[nextFocusIndex]?.focus();
    }
  };

  const handleCodeChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    setTwoFACode((prevCode) => {
      const newCode = [...prevCode];
      newCode[index] = value;
      return newCode;
    });

    if (value.length > 0 && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && twoFACode[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === "Delete" && twoFACode[index] === "" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === "Enter" && twoFACode.every((digit) => digit !== "")) {
      handle2FASubmit();
    }
  };

  const handleResendCode = async () => {
    setTwoFAError("");
    setTwoFASuccess("");
    setResendLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setTwoFASuccess(t("Code resent successfully!"));
    } catch (error) {
      setTwoFAError(
        t("Failed to resend the verification code. Please try again.")
      );
    } finally {
      setResendLoading(false);
    }
  };

  const isCodeComplete = twoFACode.every((digit) => digit !== "");

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="2fa-modal">
      <div className={`twofa-modal ${darkMode ? "dark-mode" : "light-mode"}`}>
        {/* Header */}
        <div className="twofa-header">
          <div className="twofa-header-icon">
            <FontAwesomeIcon icon={faShieldHalved} />
          </div>
          <h1 className="twofa-title">{t("Two-Factor Authentication")}</h1>
          <p className="twofa-subtitle">
            {t("Enter the 6-digit code to verify your identity")}
          </p>
        </div>

        {/* Divider */}
        <div className="twofa-divider"></div>

        {/* Email notification badge */}
        <div className="twofa-email-badge">
          <FontAwesomeIcon icon={faEnvelope} className="twofa-email-icon" />
          <span>
            {t(
              "A verification code has been sent to your email. The code will expire in 30 minutes."
            )}
          </span>
        </div>

        {/* Dev note */}
        <div className="twofa-dev-note">
          <span className="twofa-dev-note-label">{t("Dev Note:")}</span>
          {t('Enter "000000" to bypass 2FA in development mode.')}
        </div>

        {/* Error message */}
        {twoFAError && (
          <div className="twofa-message twofa-message--error">
            <FontAwesomeIcon icon={faCircleExclamation} />
            <span>{twoFAError}</span>
          </div>
        )}

        {/* Success message */}
        {twoFASuccess && (
          <div className="twofa-message twofa-message--success">
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>{twoFASuccess}</span>
          </div>
        )}

        {/* Code inputs */}
        <form onSubmit={handle2FASubmit} onPaste={handleCodePaste}>
          <div className="twofa-code-container">
            {twoFACode.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={`twofa-code-input ${digit ? "has-value" : ""} ${
                  verifySuccess ? "success" : ""
                }`}
                autoFocus={index === 0}
                autoComplete="one-time-code"
              />
            ))}
          </div>

          {/* Verify button */}
          <ActionButton
            type="submit"
            variant="secondary"
            disabled={!isCodeComplete || loading || verifySuccess}
            loading={loading && !verifySuccess}
            icon={verifySuccess ? faCheck : null}
            className="twofa-submit-btn"
          >
            {verifySuccess
              ? t("Verified!")
              : loading
              ? t("Verifying...")
              : t("Verify Code")}
          </ActionButton>
        </form>

        {/* Resend section */}
        <div className="twofa-resend">
          {resendLoading ? (
            <span className="twofa-resend-loading">
              <span className="twofa-resend-spinner"></span>
              {t("Sending...")}
            </span>
          ) : (
            <p className="twofa-resend-text">
              {t("Didn't receive the code?")}{" "}
              <button
                type="button"
                className="twofa-resend-link"
                onClick={handleResendCode}
              >
                <FontAwesomeIcon icon={faRotateRight} />
                {t("Resend Code")}
              </button>
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default TwoFactorModal;
